import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Building2, CheckCircle2, Shield, Users, Briefcase, ArrowRight, Star } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ company: '', contact_name: '', email: '', website: '', reason: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/partners/apply`, form, { withCredentials: true });
      setSubmitted(true);
    } catch {
      alert('Application submitted! We will review and get back to you.');
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Become an Employer Partner — Hire Our Graduates"
        description="Join the Institute of Analytics USA Preferred Employer network. Priority access to job-ready AI/ML/DS graduates, branded job postings, and AI-matched candidate referrals."
        path="/partners"
      />
      <div className="max-w-7xl mx-auto px-6 py-24">
        {/* Hero */}
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Employer Partnerships</p>
        <h1 data-testid="partners-heading" className="text-4xl md:text-5xl font-black tracking-tighter mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Hire Our Best Graduates.
        </h1>
        <p className="text-lg text-white/80 mb-20 max-w-3xl leading-relaxed">
          Partner with Institute of Analytics USA&trade; to access a pipeline of job-ready AI, ML & Data Science talent.
          Preferred Employers get priority access to graduates, branded job postings, and direct candidate referrals.
        </p>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#262626] mb-20">
          {[
            { icon: Shield, title: "Preferred Employer Badge", desc: "Your job postings display the IoA USA\u2122 Preferred Employer badge, signaling trust and quality to candidates." },
            { icon: Star, title: "Priority Graduate Access", desc: "Be first to review new graduates. Receive curated candidate profiles matched to your open positions." },
            { icon: Building2, title: "Branded Presence", desc: "Your company featured on our Partners page and visible to all students and prospective enrollees." },
          ].map((b, i) => (
            <div key={i} className="p-8 border-r border-b border-[#262626] bg-[#111111]">
              <b.icon className="w-16 h-16 text-[#0077B6] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold tracking-tight mb-3 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{b.title}</h3>
              <p className="text-base text-zinc-300 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="mb-20">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>How It Works</p>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-10 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Simple Partnership Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-[#262626]">
            {[
              { step: "01", title: "Apply", desc: "Fill out the partner application form below with your company details." },
              { step: "02", title: "Review", desc: "Our team reviews your application and verifies company credentials." },
              { step: "03", title: "Onboard", desc: "Receive your Preferred Employer badge and access to the graduate pipeline." },
              { step: "04", title: "Hire", desc: "Post jobs, browse graduates, and receive AI-matched candidate letters." },
            ].map((s, i) => (
              <div key={i} className="p-8 border-r border-[#262626] bg-[#0A0A0A]">
                <span className="text-4xl font-black text-[#F59E0B] font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.step}</span>
                <h3 className="text-lg font-semibold mt-3 mb-2 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{s.title}</h3>
                <p className="text-base text-zinc-300">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Application Form */}
        {submitted ? (
          <div data-testid="partner-success" className="border border-[#262626] bg-[#111111] p-12 text-center">
            <CheckCircle2 className="w-20 h-20 text-green-400 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-2xl font-bold text-[#0077B6] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Application Received!</h2>
            <p className="text-base text-zinc-300">Thank you for your interest. Our team will review your application and reach out within 2-3 business days.</p>
          </div>
        ) : (
          <div className="border border-[#262626] bg-[#111111] p-10">
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Become a Partner</p>
            <h2 className="text-2xl font-bold tracking-tight mb-8 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Apply for Preferred Employer Status</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Company Name</label>
                <input data-testid="partner-company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="Your company name" />
              </div>
              <div>
                <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Contact Name</label>
                <input data-testid="partner-contact" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="Your name" />
              </div>
              <div>
                <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Email</label>
                <input data-testid="partner-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="contact@company.com" />
              </div>
              <div>
                <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Company Website</label>
                <input data-testid="partner-website" value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="https://company.com" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Why Partner With Us?</label>
                <textarea data-testid="partner-reason" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={4} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500 resize-none" placeholder="Tell us about your hiring needs and how our graduates can help..." />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" disabled={submitting} data-testid="partner-submit" className="brand-btn text-base px-8 py-3 disabled:opacity-50 flex items-center gap-2">
                  {submitting ? 'Submitting...' : 'Submit Application'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
